import { EditablePreviewWorld, ObjectRuntimeState } from '../types'
import { pickRandom } from './primitives/pickRandom'
import { BehaviorHandler, MissionBehaviors } from './types'

/**
 * RJF02（滑り台とボウリングピン）の環境ビヘイビア。
 *
 * テンプレート rbxl の環境スクリプトを移植する:
 * - ServerScriptService/SetScript + ColorScript: 起動時に Pin Model 配下の
 *   各ピンへ `BrickColor.Random()` を適用し、ピンをカラフルにする。
 *   （すべり台の Touched → Humanoid.Sit はプレビューでは物理側の
 *   滑走モード（isSliding）が同じ体験を実装している。）
 * - 実機のピンは非アンカーの自由な剛体で、滑走してきたキャラクターが
 *   ぶつかると物理で倒れる。プレビューのシーン由来 born-unanchored 部品は
 *   ウェルド集合の崩壊対策で Fixed 据え置きのため、起動時にピンだけを
 *   実行時生成（clone と同じ扱い）として作り直し、動的剛体に乗せ換える。
 */

/** 寸法の近似一致（rbxl 由来の浮動小数のゆらぎを吸収）。 */
const nearly = (a: number, b: number): boolean => Math.abs(a - b) < 0.01

/**
 * ボウリングピンの判定。Pin Model 由来の細長い cylinder
 * （Size 5×1×0.5、非アンカー）だけが該当する。名前は 'Part#N' で
 * 順序依存のため、形状・全 3 軸の寸法で特定する。
 */
const isBowlingPin = (state: ObjectRuntimeState): boolean =>
  state.shape === 'cylinder' &&
  !state.anchored &&
  nearly(state.size.x, 5) &&
  nearly(state.size.y, 1) &&
  nearly(state.size.z, 0.5)

/**
 * ピンに塗るランダム色の候補。実機は BrickColor.Random()（全パレット）だが、
 * 「カラフルなピン」の見た目を確実に再現するため鮮やかな BrickColor を使う。
 * （export はテスト用。）
 */
export const PIN_COLORS: ReadonlyArray<string> = [
  '#C4281C', // Bright red
  '#F5CD30', // Bright yellow
  '#0D69AC', // Bright blue
  '#4B974B', // Bright green
  '#DA8541', // Bright orange
  '#6B327C', // Bright violet
  '#FF66CC', // Hot pink
  '#04AFEC', // Cyan
]

/** 動的剛体として作り直したピンの id 接尾辞（export はテスト用）。 */
export const DYNAMIC_PIN_ID_SUFFIX = '-dynamic-pin'

/**
 * 作り直し時にピンを持ち上げる量（studs。export はテスト用）。
 *
 * ピンの rbxl 座標は実機の地形表面（ボクセルの等値面）基準で、プレビューの
 * 地形表面はそれよりわずかに高いため、そのままだと底が約 0.7 studs めり込む。
 * Fixed のままなら無害だが、動的剛体はめり込み解消の押し出しを受け、細長い
 * ピンは衝突前に勝手に倒れてしまう。めり込み量より大きく持ち上げてから
 * 落とし、プレビューの地形表面に自然に立たせる（見た目も埋まらず正しくなる）。
 */
export const PIN_RELEASE_LIFT = 1

/** ランダムなピン色を 1 つ返す。 */
const randomPinColor = (): string => pickRandom(PIN_COLORS)

/**
 * 作り直し先の id を返す。万一既存 id と衝突する場合は連番を足して避ける
 * （destroy 後に addObject が重複 id で throw すると元ピンだけ消えるため）。
 */
const nextDynamicPinId = (
  world: EditablePreviewWorld,
  baseId: string,
): string => {
  let candidate = `${baseId}${DYNAMIC_PIN_ID_SUFFIX}`
  for (let i = 2; world.exists(candidate); i += 1) {
    candidate = `${baseId}${DYNAMIC_PIN_ID_SUFFIX}-${i}`
  }
  return candidate
}

/**
 * 起動時にピンをランダム色の動的剛体として作り直す
 * （SetScript + ColorScript 相当 + 実機の非アンカー物理の再現）。
 *
 * destroy → addObject で実行時生成扱いにすると、物理側の Anchored 解除
 * 遷移（clone と同じ経路）で動的剛体になり、滑走してきたアバターの衝突
 * （KCC の applyImpulsesToDynamicBodies）で倒れる・弾かれる・ピン同士で
 * 連鎖する。getState はイテレーション中の destroy/addObject で変化する
 * ため、先にスナップショットを取ってから処理する。
 * 作り直し済みのピン（id が接尾辞付き）は再実行時に増殖させず、
 * 色の塗り直しだけ行う（冪等性の担保）。
 */
const setupPins: BehaviorHandler = ({ world }) => {
  const entries = [...world.getState()]
  for (const [id, state] of entries) {
    if (!isBowlingPin(state)) {
      continue
    }
    if (id.includes(DYNAMIC_PIN_ID_SUFFIX)) {
      // 作り直し済み（連番付き含む）。色だけ塗り直す。
      world.setColor(id, randomPinColor())
      continue
    }
    world.destroy(id)
    world.addObject({
      id: nextDynamicPinId(world, id),
      name: state.name,
      // めり込み防止の持ち上げ（PIN_RELEASE_LIFT 参照）。落下して接地する。
      position: {
        x: state.position.x,
        y: state.position.y + PIN_RELEASE_LIFT,
        z: state.position.z,
      },
      size: state.size,
      color: randomPinColor(),
      material: state.material,
      rotationY: state.rotationY,
      orientation: state.orientation,
      transparency: state.transparency,
      canCollide: state.canCollide,
      canTouch: state.canTouch,
      isStatic: false,
      shape: state.shape,
    })
  }
}

export const createRjf02Behaviors = (): MissionBehaviors => ({
  onPlay: [setupPins],
})
